import React, { useState } from 'react';
import styled from 'styled-components';
import ExperienceTimeline from './ExperienceTimeline';
import ProjectGrid from './ProjectGrid';
import data from './data';
import './App.css';
import ParticleBackground from './ParticleBackground';
import About from './About';
import Contact from './Contact';
import Resume from './Resume';
import { FaGithub, FaLinkedin } from 'react-icons/fa';
import { Helmet } from 'react-helmet';
import BootSequence from './BootSequence';
import DesktopMode from './DesktopMode';

/* ============================================
   STYLED COMPONENTS
   ============================================ */

const TerminalContainer = styled.div`
  font-family: var(--font-sans, 'Inter', sans-serif);
  color: var(--text-primary, #e2e8f0);
  padding: 0 40px 40px 40px;
  width: ${props => props.maximized ? '100%' : '95%'};
  max-width: ${props => props.maximized ? '100%' : '1200px'};
  margin: ${props => props.maximized ? '0 auto' : '3vh auto'};
  box-sizing: border-box;
  border: ${props => props.maximized ? 'none' : '1px solid rgba(148, 163, 184, 0.12)'};
  border-radius: ${props => props.maximized ? '0' : '12px'};
  background: rgba(15, 23, 42, ${props => props.maximized ? '0.95' : '0.4'});
  backdrop-filter: blur(8px);
  box-shadow: ${props => props.maximized ? 'none' : '0 8px 32px rgba(0, 0, 0, 0.3)'};
  position: relative;
  min-height: ${props => props.maximized ? '100vh' : 'auto'};
  transition: all 0.3s ease;

  @media (max-width: 768px) {
    padding: 0 16px 24px 16px;
    width: 98%;
    margin: 1vh auto;
  }
`;

const TerminalTitleBar = styled.div`
  display: flex;
  align-items: center;
  padding: 14px 8px;
  margin: 0 -40px;
  padding-left: 48px;
  padding-right: 48px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.1);
  margin-bottom: 0;

  @media (max-width: 768px) {
    margin: 0 -16px;
    padding-left: 24px;
    padding-right: 24px;
  }
`;

const TrafficLights = styled.div`
  display: flex;
  gap: 8px;
  margin-right: 16px;
`;

const TrafficLight = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.color};
  opacity: 0.8;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: ${props => props.clickable ? '1' : '0.8'};
    transform: ${props => props.clickable ? 'scale(1.15)' : 'none'};
  }
`;

const TerminalTitle = styled.span`
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.75rem;
  color: var(--text-muted, #64748b);
  flex: 1;
  text-align: center;
`;

const SocialIcons = styled.div`
  display: flex;
  gap: 10px;

  a {
    color: var(--text-muted, #64748b);
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    padding: 4px;

    &:hover {
      color: var(--accent-cyan, #22d3ee);
      transform: translateY(-1px);
    }
  }
`;

const Header = styled.header`
  text-align: center;
  padding: 60px 20px 40px;

  @media (max-width: 768px) {
    padding: 40px 10px 30px;
  }
`;

const Name = styled.h1`
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 2.8rem;
  font-weight: 700;
  color: var(--text-primary, #e2e8f0);
  margin-bottom: 12px;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Tagline = styled.p`
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.95rem;
  color: var(--text-muted, #64748b);
  margin: 0;
  line-height: 1.5;

  span {
    color: var(--accent-cyan, #22d3ee);
  }

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const Nav = styled.nav`
  display: flex;
  justify-content: center;
  padding: 12px 0;
  position: sticky;
  top: 0;
  z-index: 1000;
  background: rgba(10, 14, 26, 0.85);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(148, 163, 184, 0.06);
  margin: 0 -40px;
  padding-left: 40px;
  padding-right: 40px;

  a {
    color: var(--text-secondary, #94a3b8);
    text-decoration: none;
    margin: 0 4px;
    padding: 8px 16px;
    font-family: var(--font-mono, 'JetBrains Mono', monospace);
    font-size: 0.8rem;
    font-weight: 500;
    transition: all 0.2s ease;
    border-radius: 6px;

    &:hover {
      color: var(--accent-cyan, #22d3ee);
      background: rgba(34, 211, 238, 0.06);
    }
  }

  @media (max-width: 768px) {
    margin: 0 -16px;
    padding: 8px 8px;
    gap: 0;

    a {
      margin: 0 2px;
      padding: 6px 10px;
      font-size: 0.7rem;
    }
  }
`;

const SectionDivider = styled.div`
  width: 60px;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34, 211, 238, 0.3), transparent);
  margin: 20px auto;
`;

const ProjectsSection = styled.section`
  padding: 40px 0;
`;

const ProjectsSectionLabel = styled.div`
  font-family: var(--font-mono, 'JetBrains Mono', monospace);
  font-size: 0.9rem;
  color: var(--text-muted, #64748b);
  margin-bottom: 30px;
  padding-left: 5%;

  .prompt {
    color: var(--accent-cyan, #22d3ee);
    margin-right: 8px;
    font-weight: 600;
  }
`;

const Footer = styled.footer`
  text-align: center;
  padding: 24px 0 12px;
  border-top: 1px solid rgba(148, 163, 184, 0.08);
  margin-top: 20px;

  p {
    font-family: var(--font-mono, 'JetBrains Mono', monospace);
    font-size: 0.75rem;
    color: var(--text-muted, #64748b);
    margin: 0;
  }

  span {
    color: var(--text-secondary, #94a3b8);
  }
`;

/* ============================================
   APP COMPONENT
   ============================================ */

function App() {
  const [isBooting, setIsBooting] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);

  const handleRedClick = () => {
    if (!isBooting) {
      setIsBooting(true);
    }
  };

  const handleMinimize = () => {
    setIsDesktop(true);
  };

  const handleRestore = () => {
    setIsDesktop(false);
  };

  const handleFullscreen = () => {
    setIsMaximized(prev => {
      const next = !prev;
      if (next) {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return next;
    });
  };

  if (isDesktop) {
    return <DesktopMode onRestore={handleRestore} />;
  }

  return (
    <>
      {isBooting && <BootSequence onComplete={() => setIsBooting(false)} />}
      <Helmet>
        <title>Dev Kansara | Software Engineer</title>
        <meta name="description" content="Portfolio of Dev Kansara, a Software Engineer specializing in full-stack development with Go, React, and cloud technologies." />
        <meta name="keywords" content="Dev Kansara, software engineer, full-stack developer, Go, Golang, React, cloud, AWS, GCP, USC, XOPS, portfolio" />
        <meta name="author" content="Dev Kansara" />
        <meta property="og:title" content="Dev Kansara | Software Engineer" />
        <meta property="og:description" content="Software Engineer building scalable full-stack systems." />
        <meta property="og:url" content="https://devkansara.github.io/dev-kansara" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Dev Kansara | Software Engineer" />
        <meta name="twitter:description" content="Software Engineer building scalable full-stack systems." />
        <meta name="twitter:site" content="@DevKansara_" />
        <link rel="canonical" href="https://devkansara.github.io/dev-kansara" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="robots" content="index, follow" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="theme-color" content="#0a0e1a" />
        <meta name="google-site-verification" content="pB7tVnXDQ3tjzT4_y6ESrJse89t8ySsVtKbB6c7IHrA" />
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "Person",
              "name": "Dev Kansara",
              "jobTitle": "Software Engineer",
              "url": "https://devkansara.github.io/dev-kansara",
              "sameAs": [
                "https://www.linkedin.com/in/devkansara",
                "https://github.com/devkansara"
              ]
            }
          `}
        </script>
      </Helmet>

      <ParticleBackground />

      <TerminalContainer maximized={isMaximized}>
        {/* Terminal Title Bar */}
        <TerminalTitleBar>
          <TrafficLights>
            <TrafficLight color="#ff5f57" clickable onClick={handleRedClick} />
            <TrafficLight color="#febc2e" clickable onClick={handleMinimize} />
            <TrafficLight color="#28c840" clickable onClick={handleFullscreen} />
          </TrafficLights>
          <TerminalTitle>portfolio@devkansara ~ %</TerminalTitle>
          <SocialIcons>
            <a href="https://github.com/devkansara/" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <FaGithub size={18} />
            </a>
            <a href="https://linkedin.com/in/devkansara/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <FaLinkedin size={18} />
            </a>
          </SocialIcons>
        </TerminalTitleBar>

        {/* Header */}
        <Header>
          <Name>Dev Kansara</Name>
          <Tagline>
            <span>$</span> whoami → <span>Software Engineer</span>
          </Tagline>
        </Header>

        {/* Navigation */}
        <Nav>
          <a href="#about">About</a>
          <a href="#experience">Experience</a>
          <a href="#projects">Projects</a>
          <a href="#resume">Resume</a>
          <a href="#contact">Contact</a>
        </Nav>

        {/* Sections */}
        <About />

        <SectionDivider />

        <ExperienceTimeline />

        <SectionDivider />

        <ProjectsSection id="projects">
          <ProjectsSectionLabel>
            <span className="prompt">$</span> ls projects/
          </ProjectsSectionLabel>
          <ProjectGrid data={data} />
        </ProjectsSection>

        <SectionDivider />

        <Resume />

        <SectionDivider />

        <Contact />

        {/* Footer */}
        <Footer>
          <p>
            <span>Dev Kansara</span> &copy; {new Date().getFullYear()}
          </p>
        </Footer>
      </TerminalContainer>
    </>
  );
}

export default App;
